/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/templates/QaNewTemplateForm.tsx
 * FILE_ID: QA-TEMPLATES-NEW-FORM-001
 * PURPOSE: Name, location, and steps for a new published library template.
 */
import { useState } from 'react'
import type { QaApi } from '../../../qa/qaApi'
import { QA_TEMPLATE_LOCATION_ORDER } from './qaTemplateCatalog'

const ARCHIVED_TEMPLATES = [
  { name: 'Residential', steps: ['Structure', 'Wet areas', 'Services'] },
  { name: 'Commercial', steps: ['Structure', 'Services', 'Finishes'] },
  { name: 'Infrastructure', steps: ['Structure', 'Services'] },
  { name: 'New Construction', steps: ['Structure', 'Envelope', 'Services'] },
  { name: 'Renovation', steps: ['Strip-out', 'Services', 'Finishes'] },
  { name: 'Remediation', steps: ['Investigation', 'Repair'] },
  // Reused from: apps/qa/src/lib/inspection/trades-and-templates.ts (QUICK_TEMPLATES)
  { name: 'New Home Inspection', steps: ['Living', 'Kitchen', 'Garage', 'Master Bedroom', 'Bedroom 2', 'Bathroom'] },
  { name: 'Commercial Fit-out', steps: ['Reception', 'Open Plan', 'Amenities'] },
  { name: 'Apartment Block', steps: ['Structure', 'Wet areas', 'Fire'] },
  { name: 'Warehouse/Industrial', steps: ['Loading Bay', 'Storage Area', 'Office'] },
]

const LOCATION_LABELS: Record<string, string> = {
  roof: 'Roof',
  floors: 'Floors',
  underslab: 'Underslab',
  walls: 'Walls',
  below_grade: 'Below grade',
  general: 'General',
  internal_wet: 'Internal wet areas',
  balcony: 'Balcony & terrace',
  planter: 'Planter boxes',
  handover: 'Handover',
  testing: 'Testing',
  required_attachments: 'Required Attachments',
}

export function QaNewTemplateForm({
  api,
  locationType,
  jobId,
  onCancel,
  onCreated,
}: {
  api: QaApi
  locationType: string | null
  jobId?: number
  onCancel: () => void
  onCreated: (created: { id: string; location_type: string }) => void
}) {
  const [name, setName] = useState('')
  const [location, setLocation] = useState(locationType || 'roof')
  const [steps, setSteps] = useState([''])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showArchived, setShowArchived] = useState(false)
  const titles = steps.map((step) => step.trim()).filter(Boolean)
  const canSave = name.trim().length > 0 && name.trim().length <= 160 && Boolean(location) && titles.length > 0 && !busy

  const save = async () => {
    if (!canSave) return
    setBusy(true)
    setError(null)
    try {
      const created = await api.createTemplate({
        name: name.trim(),
        trade: 'Waterproofing',
        system: '',
        location_type: location,
        ...(jobId != null ? { job_id: jobId } : {}),
        sections: [{ title: name.trim(), items: titles.map((title) => ({ title })) }],
      })
      try {
        await api.publishTemplate(created.id)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not publish the template.')
        setBusy(false)
        return
      }
      onCreated({ id: created.id, location_type: location })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the template.')
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3">
      <label className="block text-sm">
        Name
        <input
          value={name}
          maxLength={160}
          aria-label="Template name"
          onChange={(event) => setName(event.target.value)}
          className="mt-1 w-full rounded-md border border-border px-2 py-1.5 text-sm"
        />
      </label>
      <label className="block text-sm">
        Location
        <select
          value={location}
          aria-label="Template location"
          onChange={(event) => setLocation(event.target.value)}
          className="mt-1 w-full rounded-md border border-border px-2 py-1.5 text-sm"
        >
          {QA_TEMPLATE_LOCATION_ORDER.map((key) => (
            <option key={key} value={key}>{LOCATION_LABELS[key]}</option>
          ))}
        </select>
      </label>
      <button
        type="button"
        onClick={() => setShowArchived(true)}
        className="w-fit min-h-[44px] text-left text-sm underline"
      >
        Archived templates
      </button>
      {showArchived ? (
        <div className="grid gap-2">
          <button type="button" onClick={() => setShowArchived(false)} className="w-fit min-h-[44px] text-sm underline">
            Back
          </button>
          {ARCHIVED_TEMPLATES.map((card) => (
            <button
              key={card.name}
              type="button"
              onClick={() => {
                setName(card.name)
                setSteps(card.steps)
                setShowArchived(false)
              }}
              className="min-h-[44px] rounded-md border border-border px-3 text-left text-sm"
            >
              {card.name}
            </button>
          ))}
        </div>
      ) : null}
      <div className="space-y-2">
        <p className="text-sm">Steps</p>
        {steps.map((step, index) => (
          <div key={index} className="flex min-w-0 items-center gap-2">
            <input
              value={step}
              maxLength={240}
              aria-label={`Step ${index + 1}`}
              onChange={(event) => setSteps((current) => current.map((row, rowIndex) => (
                rowIndex === index ? event.target.value : row
              )))}
              className="min-w-0 flex-1 rounded-md border border-border px-2 py-1.5 text-sm"
            />
            <button
              type="button"
              aria-label={`Remove step ${index + 1}`}
              disabled={steps.length === 1}
              onClick={() => setSteps((current) => current.filter((_, rowIndex) => rowIndex !== index))}
              className="shrink-0 px-2 text-xs text-muted-foreground disabled:opacity-40"
            >
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => setSteps((current) => [...current, ''])}
          className="text-xs font-medium underline"
        >
          Add step
        </button>
      </div>
      {error ? <p role="alert" className="text-xs text-destructive">{error}</p> : null}
      <div className="mt-auto flex items-center justify-end gap-2">
        <button type="button" onClick={onCancel} className="rounded-md border border-border px-3 py-1.5 text-sm">
          Cancel
        </button>
        <button
          type="button"
          disabled={!canSave}
          onClick={() => void save()}
          className="rounded-md bg-foreground px-3 py-1.5 text-sm text-background disabled:opacity-40"
        >
          Save
        </button>
      </div>
    </div>
  )
}
